import * as React from 'react';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { Catalog } from '@liveauctioneers/hammer-ui-liveauctioneers/lotDetail';
import {
    getCatalogCurrency,
    getCatalogEndTime,
    getCatalogIsCatalogOnly,
    getCatalogIsLAPayments,
    getCatalogIsTimedPlus,
    getCatalogPaymentProvider,
    getCatalogSellerId,
    getCatalogSellerName,
    getCatalogStartTime,
    getCatalogStatus,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCurrencyConversion } from '@/redux/modules/currency';
import { getHasValidCardOnFile } from '@/redux/modules/creditCard';
import { getItemModelCatalogId } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveCatalogStatus } from '@/redux/modules/console';
import { getRegistrationNotify } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getSellerPhoneNumber } from '@/redux/modules/sellerDetail';
import { preferredCurrencySelector } from '@/redux/modules/bidder/preferences/bidderPreferences.selectors';
import { useAppSelector } from '@/redux/hooks';
import { useMemo } from 'react';

type Props = {
    itemId: number;
};

export const useGetBidCanvasCatalogData = ({ itemId }: Props): Catalog => {
    const catalogId = useAppSelector((state) => getItemModelCatalogId(state, itemId));
    const status = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const currency = useAppSelector((state) => getCatalogCurrency(state, catalogId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const livePaymentsEnabled = useAppSelector((state) => getCatalogIsLAPayments(state, catalogId));
    const paymentProvider = useAppSelector((state) => getCatalogPaymentProvider(state, catalogId));
    const saleEndTs = useAppSelector((state) => getCatalogEndTime(state, catalogId));
    const saleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const auctioneerName = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const auctioneerPhoneNumber = useAppSelector((state) => getSellerPhoneNumber(state, sellerId));
    const hasCreditCardOnFile = useAppSelector((state) => getHasValidCardOnFile(state, paymentProvider));
    const preferredCurrency = useAppSelector(preferredCurrencySelector);
    const preferredCurrencyConversion = useAppSelector((state) => getCurrencyConversion(state, preferredCurrency));
    const sourceCurrencyConversion = useAppSelector((state) => getCurrencyConversion(state, currency));
    const notify = useAppSelector((state) => getRegistrationNotify(state, catalogId));
    const isTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));

    // const auctionStatus = getAuctionStatus(catalogStatus, status);
    const auctionStatus = React.useMemo((): Catalog['auctionStatus'] => {
        switch (catalogStatus) {
            case AuctionStatus.Paused:
                return 'paused';

            case AuctionStatus.Online:
                return 'upcoming';

            case AuctionStatus.Live:
                // USE LIVE AUCTION STATUS INSTEAD
                switch (status) {
                    case AuctionStatus.Paused:
                        return 'paused';
                    case AuctionStatus.Done:
                    case AuctionStatus.Ended:
                        return 'ended';
                    case AuctionStatus.Online:
                        return 'upcoming';
                    case AuctionStatus.Upcoming:
                        return 'upcoming';
                    case AuctionStatus.Live:
                    default:
                        return 'live';
                }

            case AuctionStatus.Ended:
            case AuctionStatus.Done:
                return 'ended';

            case AuctionStatus.Upcoming:
            default:
                return 'upcoming';
        }
    }, [catalogStatus, status]);

    const auctionEndTime = Boolean(saleEndTs) ? saleEndTs * 1000 : 0;
    const auctionStartTime = Boolean(saleStartTs) ? saleStartTs * 1000 : 0;
    const sourceCurrencyCode = currency as Catalog['currency']['sourceCurrencyCode'];

    return useMemo(() => {
        return {
            auctioneerName,
            auctioneerPhoneNumber,
            auctionEndTime,
            auctionStartTime,
            auctionStatus,
            catalogId,
            currency: {
                preferredCurrencyConversion,
                sourceCurrencyCode,
                sourceCurrencyConversion,
            },
            isCatalogOnly,
            isFollowed: notify,
            isTimedPlus,
            livePayments: {
                hasCreditCardOnFile,
                livePaymentsEnabled,
            },
        };
    }, [
        auctionEndTime,
        auctionStartTime,
        auctionStatus,
        auctioneerName,
        auctioneerPhoneNumber,
        catalogId,
        hasCreditCardOnFile,
        isCatalogOnly,
        isTimedPlus,
        livePaymentsEnabled,
        notify,
        preferredCurrencyConversion,
        sourceCurrencyCode,
        sourceCurrencyConversion,
    ]);
};
